/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useContext, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import {
  Button,
  Modal,
  Text,
  Input,
  useInput,
  Grid,
  Loading,
} from '@nextui-org/react'

import { Label } from 'reactstrap'
import { func } from 'prop-types'
import { FeathersContext } from '../../../services/feathers'

import {
  initialState,
  setLikes,
  setTalent,
} from '../../../redux/reducers/talentSlice'
import { resetTalent } from '../../../api/talents.api'
import useGetLikes from '../../../hooks/useGetLikes'

const propTypes = {
  closeDrawer: func.isRequired,
}
/**
 *  Main profile view for user
 * */
function SettingsDrawer({ closeDrawer }) {
  const { t } = useTranslation(['common', 'talentArea'], {
    useSuspense: false,
  })
  const feathers = useContext(FeathersContext)

  const dispatch = useDispatch()
  const talent = useSelector((state) => state.talent)
  const { firstname, lastname, phoneNumber, email, id: talentId } = talent
  const [emailInput, setEmailInput] = useState(email)
  const [phoneInput, setPhoneInput] = useState(phoneNumber)
  const [isValidMail, setIsValidMail] = useState(false)
  const [isValidPhone, setIsValidPhone] = useState(false)
  const [firstnameInput, setFirstnameInput] = useState(firstname)
  const [lastnameInput, setLastnameInput] = useState(lastname)
  const [loading, setLoading] = useState(false)
  const { refetchLikes } = useGetLikes()

  const {
    value: emailValue,
    reset: emailReset,
    bindings: emailBindings,
  } = useInput(emailInput)

  const {
    value: phoneValue,
    reset: phoneReset,
    bindings: phoneBindings,
  } = useInput(phoneInput)

  const validateEmail = (value) =>
    value.match(/[a-zA-Z0-9_.]+@[a-zA-Z0-9]+\.[a-zA-Z]{2,4}/i)

  const validatePhoneNumber = (value) =>
    value.match(/^((\+)33|0|0033)[1-9](\d{2}){4}$/g) || value === ''

  const validEmail = useMemo(() => {
    if (!emailValue)
      return {
        text: '',
        color: '',
      }
    const isValidEmail = validateEmail(emailValue)
    return {
      text: isValidEmail ? 'Correct email' : 'Enter a valid email',
      color: isValidEmail ? 'success' : 'error',
      setIsValidMail: isValidEmail
        ? setIsValidMail(true)
        : setIsValidMail(false),
    }
  }, [emailValue])

  const validPhone = useMemo(() => {
    if (!phoneValue)
      return {
        text: '',
        color: '',
      }
    const isValidPhone = validatePhoneNumber(phoneValue)
    return {
      text: isValidPhone ? 'Correct phone' : 'Enter a valid phone',
      color: isValidPhone ? 'success' : 'error',
      setIsValidPhone: isValidPhone
        ? setIsValidPhone(true)
        : setIsValidPhone(false),
    }
  }, [phoneValue])

  const handleResetTalent = useCallback(async () => {
    await resetTalent(talent.id)
    dispatch(setLikes(initialState.likes))
  }, [dispatch, talent.id])

  const updateTalent = async (value) => {
    setLoading(true)
    try {
      const response = await feathers.service('talents').patch(talentId, {
        ...value,
      })
      if (response) {
        setLoading(false)
        dispatch(setTalent(response))
        refetchLikes()
        closeDrawer()
      }
    } catch (error) {
      console.error(error)
    }
  }

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  return (
    <div>
      <Modal.Header>
        <Text id="modal-title" size={18}>
          Mon compte
        </Text>
      </Modal.Header>
      <Modal.Body>
        <form style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <Label>
            <Grid.Container gap={3}>
              <Grid xs={12}>
                <Input
                  clearable
                  fullWidth
                  animated={false}
                  shadow={false}
                  required
                  label="Name"
                  placeholder="Enter your name"
                  value={capitalizeFirstLetter(lastname)}
                  onChange={(e) => setLastnameInput(e.target.value)}
                />
              </Grid>
              <Grid xs={12}>
                <Input
                  clearable
                  fullWidth
                  animated={false}
                  shadow={false}
                  required
                  label="Prénom"
                  placeholder="Enter your name"
                  value={capitalizeFirstLetter(firstname)}
                  onChange={(e) => setFirstnameInput(e.target.value)}
                />
              </Grid>
              <Grid xs={12}>
                <Input
                  {...emailBindings}
                  clearable
                  animated={false}
                  shadow={false}
                  fullWidth
                  onClearClick={emailReset}
                  status={validEmail.color}
                  color={validEmail.color}
                  helperColor={validEmail.color}
                  helperText={validEmail.text}
                  type="email"
                  label={t('common:email')}
                  placeholder="hollo@hollo.fr"
                  value={emailInput}
                />
              </Grid>
              <Grid xs={12}>
                <Input
                  {...phoneBindings}
                  clearable
                  animated={false}
                  shadow={false}
                  fullWidth
                  onClearClick={phoneReset}
                  status={validPhone.color}
                  color={validPhone.color}
                  helperColor={validPhone.color}
                  helperText={validPhone.text}
                  type="phone"
                  label={t('common:phoneNumber')}
                  placeholder="0123456789"
                  value={phoneInput}
                />
              </Grid>

              <Grid xs={12} justify="center">
                {!loading ? (
                  <Button
                    disabled={
                      !isValidMail ||
                      !validatePhoneNumber(phoneValue) ||
                      !firstnameInput ||
                      !lastnameInput
                    }
                    onClick={() => {
                      updateTalent({
                        lastname: lastnameInput,
                        firstname: firstnameInput,
                        email: emailValue,
                        phoneNumber: phoneValue,
                      })
                    }}
                  >
                    submit
                  </Button>
                ) : (
                  <Button disabled bordered color="primary" css={{ px: '$13' }}>
                    <Loading color="currentColor" size="sm" />
                  </Button>
                )}
              </Grid>
            </Grid.Container>
          </Label>
        </form>
      </Modal.Body>
      <Modal.Footer>
        {(!process.env.REACT_APP_ENV ||
          process.env.REACT_APP_ENV === 'staging') && (
          <Button onClick={handleResetTalent}>RESET Talent likes</Button>
        )}
      </Modal.Footer>
    </div>
  )
}

SettingsDrawer.propTypes = propTypes
export default SettingsDrawer
